import { useEffect, useRef, type ReactNode } from 'react';

/** System serif stack: film-title typography without downloading a font. */
export const FILM_SERIF = "'Iowan Old Style', 'Palatino Linotype', Palatino, 'Book Antiqua', Georgia, serif";

interface Common {
  title: string;
  seasonTitle: string | null;
  /** Only dates the files themselves recorded; null when none did. */
  dateLine: string | null;
  location: string | null;
  /** AI story opening (opening card) or closing (ending card), already rendered. */
  storyLine?: ReactNode;
}

/**
 * Opening card. Rises in once, holds, then dissolves while the first memory
 * comes up underneath (`leaving`). If audio needs a gesture first, it waits
 * with a Play button instead of running on its own.
 */
export function OpeningCard({
  title,
  seasonTitle,
  dateLine,
  location,
  storyLine,
  storyLineTiming = 'delayed',
  backdrop,
  leaving,
  waitingForPlay,
  animationChoice,
  recording = false,
  onPlay,
  onSkip,
  onGone,
}: Common & {
  /**
   * When the story opening appears: 'delayed' after the title (no voice);
   * 'wait' hidden until its voice starts; 'now' at once (the voice has started).
   */
  storyLineTiming?: 'delayed' | 'wait' | 'now';
  /**
   * The first memory, behind the title: it comes forward over `durationMs`
   * (the length of the card), so the film flows out of it instead of black.
   */
  backdrop?: { url: string; durationMs: number } | null;
  leaving: boolean;
  waitingForPlay: boolean;
  /** First film: ask how photos should move before starting. */
  animationChoice: { hasDepth: boolean; onChoose: (v: 'still' | 'motion' | 'depth') => void } | null;
  /** Exporting: no buttons on the card, so they are not in the video. */
  recording?: boolean;
  onPlay: () => void;
  onSkip: () => void;
  onGone: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!leaving) return;
    const t = window.setTimeout(onGone, 1300);
    return () => window.clearTimeout(t);
  }, [leaving, onGone]);

  return (
    <div
      ref={ref}
      className={`film-card absolute inset-0 z-20 flex flex-col items-center justify-center bg-[#0b0907] px-6 text-center transition-opacity duration-[1200ms] ease-out ${
        leaving ? 'pointer-events-none opacity-0' : 'opacity-100'
      }`}
    >
      {backdrop && (
        // Behind everything on the card (the card is its own stacking layer).
        <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" aria-hidden="true">
          <img
            src={backdrop.url}
            alt=""
            aria-hidden="true"
            draggable={false}
            className="film-backdrop pointer-events-none absolute inset-0 h-full w-full object-cover"
            style={{ animationDuration: `${backdrop.durationMs}ms` }}
          />
          {/* Keeps the title and the line readable over the picture. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{ background: 'radial-gradient(ellipse at center, rgba(11,9,7,0.55) 0%, rgba(11,9,7,0.35) 45%, rgba(11,9,7,0.75) 100%)' }}
          />
        </div>
      )}
      <div className="relative flex flex-col items-center [text-shadow:0_2px_18px_rgba(0,0,0,0.75)]">
        <CardText title={title} seasonTitle={seasonTitle} dateLine={dateLine} location={location} />
      </div>
      {storyLine && (
        <div
          key={storyLineTiming === 'wait' ? 'wait' : 'shown'}
          className={`relative mt-8 max-w-2xl text-[#f3ece2]/90 [text-shadow:0_1px_12px_rgba(0,0,0,0.8)] ${storyLineTiming === 'wait' ? 'invisible' : 'film-rise'}`}
          style={{ animationDelay: storyLineTiming === 'delayed' ? '1400ms' : '0ms' }}
          aria-hidden={storyLineTiming === 'wait'}
        >
          {storyLine}
        </div>
      )}
      {recording ? null : animationChoice ? (
        <AnimationChooser {...animationChoice} />
      ) : waitingForPlay ? (
        <button
          onClick={onPlay}
          className="focus-ring film-rise mt-12 flex items-center gap-3 rounded-full bg-[#f3ece2] px-7 py-3 text-sm font-semibold text-[#0b0907] transition hover:bg-white"
          style={{ animationDelay: '900ms' }}
          autoFocus
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
            <path d="M8 5v14l11-7z" />
          </svg>
          Play film
        </button>
      ) : (
        !leaving && (
          <button
            onClick={onSkip}
            className="focus-ring film-rise absolute right-5 bottom-[max(1.25rem,env(safe-area-inset-bottom))] rounded-full px-4 py-2 text-xs text-[#f3ece2]/45 transition hover:text-[#f3ece2]"
            style={{ animationDelay: '1200ms' }}
          >
            Skip intro
          </button>
        )
      )}
    </div>
  );
}

function AnimationChooser({ hasDepth, onChoose }: NonNullable<Parameters<typeof OpeningCard>[0]['animationChoice']>) {
  const options = [
    ...(hasDepth ? [{ value: 'depth' as const, label: 'Bring photos to life', hint: 'A slow 3D camera move through each photo' }] : []),
    { value: 'motion' as const, label: hasDepth ? 'Gentle motion' : 'Bring photos to life', hint: 'A slow zoom and pan' },
    { value: 'still' as const, label: 'Keep photos still', hint: 'Soft dissolves, no movement' },
  ];
  return (
    <div className="film-rise mt-12 flex w-full max-w-2xl flex-col items-center" style={{ animationDelay: '900ms' }}>
      <p className="mb-4 text-sm text-[#f3ece2]/70">How should your photos play?</p>
      <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
        {options.map((o, i) => (
          <button
            key={o.value}
            onClick={() => onChoose(o.value)}
            aria-description={o.hint}
            autoFocus={i === 0}
            className={`focus-ring rounded-full px-5 py-2.5 text-sm font-semibold whitespace-nowrap transition ${
              i === 0
                ? 'bg-[#f3ece2] text-[#0b0907] hover:bg-white'
                : 'bg-[#f3ece2]/10 text-[#f3ece2] ring-1 ring-[#f3ece2]/15 hover:bg-[#f3ece2]/20'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      <p className="mt-4 text-xs text-[#f3ece2]/40">You can change this anytime in the player settings.</p>
    </div>
  );
}

function CardText({ title, seasonTitle, dateLine, location }: Common) {
  return (
    <>
      {seasonTitle && (
        <p className="film-rise mb-5 text-[13px] text-[#f3ece2]/50" style={{ animationDelay: '150ms' }}>
          {seasonTitle}
        </p>
      )}
      <h1
        className="film-rise max-w-[20ch] text-[clamp(2.4rem,7vw,5rem)] [text-wrap:balance] leading-[1.05] font-normal tracking-[-0.01em] text-[#f3ece2]"
        style={{ fontFamily: FILM_SERIF, animationDelay: '300ms' }}
      >
        {title}
      </h1>
      <span className="film-rule mt-7 mb-6 block h-px w-14 bg-[#f3ece2]/40" style={{ animationDelay: '800ms' }} />
      {dateLine && (
        <p className="film-rise text-sm text-[#f3ece2]/70 tabular-nums" style={{ animationDelay: '1000ms' }}>
          {dateLine}
        </p>
      )}
      {location && (
        <p className="film-rise mt-1 text-sm text-[#f3ece2]/50" style={{ animationDelay: '1150ms' }}>
          {location}
        </p>
      )}
    </>
  );
}

/** Closing card over the final frame: replay, back, or the next story. */
export function EndingCard({
  title,
  seasonTitle,
  dateLine,
  location,
  storyLine,
  storyLineTiming = 'delayed',
  memoryCount,
  nextTitle,
  full,
  recording = false,
  download,
  onReplay,
  onBack,
  onNext,
}: Common & {
  /** As on the opening card: 'wait' hides the closing line until its voice starts. */
  storyLineTiming?: 'delayed' | 'wait' | 'now';
  memoryCount: number;
  nextTitle: string | null;
  /** false = compact end bar (ending card turned off in settings). */
  full: boolean;
  /** Exporting: no buttons, so they are not in the video. */
  recording?: boolean;
  /** "Download video" (story films). */
  download?: ReactNode;
  onReplay: () => void;
  onBack: () => void;
  onNext: (() => void) | null;
}) {
  const actions = (
    <div className="film-rise flex flex-wrap items-center justify-center gap-3" style={{ animationDelay: full ? '1100ms' : '0ms' }}>
      <button
        onClick={onReplay}
        className="focus-ring rounded-full bg-[#f3ece2] px-6 py-2.5 text-sm font-semibold text-[#0b0907] transition hover:bg-white"
        autoFocus
      >
        Watch again
      </button>
      {onNext && nextTitle && (
        <button
          onClick={onNext}
          className="focus-ring max-w-[16rem] truncate rounded-full bg-[#f3ece2]/10 px-6 py-2.5 text-sm font-semibold text-[#f3ece2] ring-1 ring-[#f3ece2]/15 transition hover:bg-[#f3ece2]/20"
        >
          Next: {nextTitle}
        </button>
      )}
      <button
        onClick={onBack}
        className="focus-ring rounded-full px-5 py-2.5 text-sm text-[#f3ece2]/65 transition hover:text-[#f3ece2]"
      >
        Back to story
      </button>
    </div>
  );

  if (!full) {
    if (recording) return null;
    return (
      <div className="absolute inset-x-0 bottom-0 z-40 bg-gradient-to-t from-[#0b0907] via-[#0b0907]/85 to-transparent px-6 pt-24 pb-[max(2rem,env(safe-area-inset-bottom))]">
        {actions}
        {download && <div className="mt-5 flex justify-center">{download}</div>}
      </div>
    );
  }

  return (
    <div className="film-fade absolute inset-0 z-40 flex flex-col items-center justify-center bg-[#0b0907]/90 px-6 text-center backdrop-blur-md">
      <CardText title={title} seasonTitle={seasonTitle} dateLine={dateLine} location={location} />
      {storyLine && (
        <div
          key={storyLineTiming === 'wait' ? 'wait' : 'shown'}
          className={`mt-6 max-w-2xl text-[#f3ece2]/80 ${storyLineTiming === 'wait' ? 'invisible' : 'film-rise'}`}
          style={{ animationDelay: storyLineTiming === 'delayed' ? '1000ms' : '0ms' }}
          aria-hidden={storyLineTiming === 'wait'}
        >
          {storyLine}
        </div>
      )}
      <p className="film-rise mt-5 mb-10 text-xs text-[#f3ece2]/40" style={{ animationDelay: '1050ms' }}>
        {memoryCount} {memoryCount === 1 ? 'memory' : 'memories'}
      </p>
      {recording ? null : actions}
      {!recording && download && (
        <div className="film-rise mt-6 flex justify-center" style={{ animationDelay: '1300ms' }}>
          {download}
        </div>
      )}
    </div>
  );
}
